import { BusinessObjectBase } from './base';
import { personalAccessTokenSchema } from '@schemas/business-objects/personal-access-token';
import type { User } from './user';
import type { Id } from '../base-types';
import type {
  MintedPersonalAccessToken,
  PersonalAccessTokenSummary,
} from '../interface-definitions/index';

export interface PersonalAccessTokenData {
  readonly id: Id;
  readonly name: string;
  readonly hash: string;
  readonly displayPrefix: string;
  readonly created?: Date;
  readonly expiresAt?: Date;
  readonly lastUsedAt?: Date;
  readonly revoked?: boolean;
  readonly userId: Id;
  readonly user?: User;
}

export interface PersonalAccessToken extends Readonly<PersonalAccessTokenData> {}
export class PersonalAccessToken extends BusinessObjectBase {
  static readonly entityName = 'PersonalAccessToken' as const;
  static readonly plural = 'personal-access-tokens';
  static schema = personalAccessTokenSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<PersonalAccessToken[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new PersonalAccessToken(data));
  }

  static async findOne(filter?: any): Promise<PersonalAccessToken | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new PersonalAccessToken(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<PersonalAccessToken> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new PersonalAccessToken(data);
  }

  static async create(
    data: Omit<PersonalAccessTokenData, 'id'>,
  ): Promise<PersonalAccessToken> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new PersonalAccessToken(result);
  }

  static async createMany(
    data: Omit<PersonalAccessTokenData, 'id'>[],
  ): Promise<PersonalAccessToken[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new PersonalAccessToken(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<PersonalAccessTokenData>,
  ): Promise<PersonalAccessToken> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new PersonalAccessToken(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async revoke(tokenId: Id): Promise<void> {
    const url = `${this.baseUrl}/${this.plural}/revoke`;
    return this.post<void>(url, { tokenId });
  }
  static async mint(
    name: string,
    expiresInDays?: number,
  ): Promise<MintedPersonalAccessToken> {
    const url = `${this.baseUrl}/${this.plural}/mint`;
    return this.post<MintedPersonalAccessToken>(url, { name, expiresInDays });
  }
  static async mine(): Promise<PersonalAccessTokenSummary[]> {
    const url = `${this.baseUrl}/${this.plural}/mine`;
    return this.get<PersonalAccessTokenSummary[]>(url);
  }
}
